import React, {PureComponent} from "react"
import {Row, Col} from "react-bootstrap"
import {RouteComponentProps} from "react-router"
import * as PropTypes from "prop-types"

import {DealListSort, DealStatus, DealCategory, DealsTag, DealType} from "@irati/types"
import History from "@irati/utils/history"
import Loading from "@irati/ui/src/Loading"
import {injectIntl, FormattedMessage, InjectIntl} from "@irati/utils/lang"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import categoriesService from "@irati/deals/services/dealCategory"
import dealsService from "@irati/deals/services/dealsGraph"
import DealListComponent, {getDealList, DealListVariables} from "@irati/deals/apollo/query/DealList"
import {hangPromise} from "@irati/utils/promise"
import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"
import {IconTypes} from "@irati/ui/src/Icon"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"
import {Regions} from "@irati-web/fields/region"
import authentication from "@irati/auth"
import {H2, H4, Body} from "@irati/ui/src/Typography"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import perksTracking from "@irati-tracking/perks/perks"
import usersService from "@irati/identity/services/users"
import Sticky from "react-sticky-el"
import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import theme from "@irati/ui/src/global/theme"
import {getFlexiPerkTag} from "@shared/tag/utils"
import {getSortOpts} from "@shared/deals/utils/deal/sortOptions"
import GlobalRewardWalletModal from "@irati-web/platform/src/components/GlobalRewardWalletModal"
import {FiltersWrapper} from "../../components/Categories/styled"

import {FilteringParams, TypeOption, fromQuery, toQuery} from "../../utils/deals/dealList"
import Categories from "../../components/Categories"
import {SORT_OPTIONS} from "../../constants/dealSort"
import DEAL_PAGES from "../../constants/dealPage"
import DealList from "../../components/DealList"
import DealTypesFilter from "../../components/DealTypesFilter"
import ShowMoreOrThatsAll from "../../components/DealList/ShowMoreOrThatsAll"
import BadCategoryOrTag, {ALL_CATEGORY} from "../../components/DealList/BadCategoryOrTag"
import {StyledSortBy, DisplayCount, Container} from "./styled"

export const DEAL_TYPES_OPTIONS = (formatMessage: FormatMessage) => [
  {
    uuid: "voucher",
    name: formatMessage({id: "perks.fields.filterBy.dealType.voucher"}),
    value: [DealType.Classic, DealType.External]
  },
  {
    uuid: "merchandise",
    name: formatMessage({id: "perks.fields.filterBy.dealType.merchandise"}),
    value: [DealType.Merchandise]
  }
]

const getTagsUuids = (tags: DealsTag[]) => {
  return tags.map((tag) => tag.uuid!)
}

const isFlexiTagSelected = (selectedTags: DealsTag[]) => {
  const flexiPerk = getFlexiPerkTag()
  return selectedTags.some((tag) => tag.uuid === flexiPerk.id)
}
const DEFAULT_SORT = DealListSort.Date

interface Context {
  appPath: string
}

interface State {
  load: Promise<any>
  variables: DealListVariables
  filters: FilteringParams
  categories: DealCategory[]
  selectedRegions?: string[]
  dealTypes: TypeOption[]
}

interface Props extends RouteComponentProps<{tags?: string; category?: string}>, InjectIntl, WithPerkOrReward {}

class Deals extends PureComponent<Props, State> {
  static defaultProps = {
    id: "deals"
  }
  static contextTypes = {
    apps: PropTypes.object.isRequired,
    appPath: PropTypes.string.isRequired
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const {category} = props.match.params
    const {dealTypes, categories, variables} = state

    const filters = fromQuery(dealTypes, categories, category)
    const {tags, type, sort} = filters

    variables.tags = getTagsUuids(tags)
    variables.regions = state.selectedRegions
    variables.types = type?.value || []
    variables.sort = sort || DEFAULT_SORT

    if (variables.tags.length === 0) {
      delete variables.tags
    }

    // hide EBD deals just when browsing "All Perks" (no tags are selected)
    variables.hideEB = !variables.tags

    return {
      variables,
      filters
    }
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      load: hangPromise(),
      variables: this.getInitialVariables(),
      filters: {},
      categories: [],
      selectedRegions: [],
      dealTypes: DEAL_TYPES_OPTIONS(props.formatMessage)
    }
  }

  getInitialVariables = () => {
    const initial = {
      statuses: [DealStatus.On, DealStatus.Unavailable],
      isRewards: this.props.isRewards,
      sort: DEFAULT_SORT,
      types: [],
      limit: 9
    }

    if (this.props.isRewards || this.props.isWallet) {
      initial.sort = DealListSort.Popularity
    }

    return initial
  }

  async componentDidMount() {
    const {isRewards, isWallet} = this.props

    perksTracking.perksViewCatalog(isRewards ? "rewards" : "perks")
    let categoryRequest
    if (isRewards) {
      categoryRequest = categoriesService.listForRewardPackage
      const {regionId} = await usersService.regionIdForUser(authentication.user().uuid!)
      this.setState({selectedRegions: [regionId]})
    } else {
      categoryRequest = categoriesService.listForCurrentPackage
    }

    const initialVariables = this.getInitialVariables()

    const load = Promise.all([dealsService.list({...initialVariables, regions: this.state.selectedRegions}), categoryRequest()]).then(
      ([rootDeals, dealCategories]) => {
        let {total} = rootDeals
        if (isWallet) {
          // as we can change the region for isWallet, this becomes out of sync quickly so hiding for now
          total = 0
        }
        this.setState(
          {
            categories: [{...ALL_CATEGORY, dealCount: total}].concat(dealCategories)
          },
          () => {
            this.updateSearchParams({})
          }
        )
      }
    )

    this.setState({load})
  }

  handleCategoryOrTagChange = (category: DealCategory, tags: DealsTag[]) => {
    let pathname = "/categories"
    let searchParams: string[] = []

    if (this.props.isWallet) {
      pathname = "/flexi-reward/categories"
    }
    if (category.url) {
      pathname += `/${category.url}`
    }

    if (tags && tags.length) {
      searchParams = tags.map((tag) => tag.url)
    }

    this.updateSearchParams({tags: searchParams}, {pathname, context: this.context})
  }

  handleNewPage = (isVisible: boolean, apollo: ApolloState<any, DealListVariables>) => {
    if (isVisible) {
      const {results, hasMore} = getDealList(apollo)
      if (hasMore) {
        // has more
        return apollo.nextPage({...this.state.variables, offset: results.length})
      }
    }
  }

  handleRegionFilterChange = (selectedRegions: string[]) => {
    tracking.walletSelectRegion(
      {regions: selectedRegions},
      {role: authentication.apps("wallet.flexiRewards.canTopUpRewardPoints") ? "Admin" : "Manager"}
    )
    this.setState({selectedRegions})
  }

  getSortOpts = () => {
    const {isPerks, isRewards} = this.props
    const isFlexiPerks = isPerks && isFlexiTagSelected(this.state.filters.tags)

    return getSortOpts(SORT_OPTIONS, {isFlexiPerks, isRewards})
  }

  updateSearchParams = (changes: {[key: string]: string | string[]}, options?: {pathname?: string; context?: Context}) => {
    const {filters} = this.state
    const currentPathname = options?.pathname || location.pathname
    const sortOptions = this.getSortOpts().map((opt) => opt.value)

    const search = toQuery(filters, changes, sortOptions)

    History.push({pathname: currentPathname, search}, options?.context)
  }

  render() {
    const {isRewards, isWallet} = this.props
    const {load, selectedRegions, categories, dealTypes, variables, filters} = this.state
    const {category, tags} = filters

    const selectedTags = category.tags.length === tags.length ? [] : tags
    const canRewardEmployees = authentication.apps("wallet.flexiRewards.canRewardEmployees")
    const showRegionFilter = isRewards && isWallet && canRewardEmployees
    const sortOptions = this.getSortOpts()
    const hasUkRegion = selectedRegions?.includes("UK")
    const showDealTypeFilter = hasUkRegion && isRewards

    return (
      <Row>
        <Container>
          <Col md={12}>
            {isRewards && (
              <>
                {!isWallet && (
                  <H2 marginTop={2}>
                    <FormattedMessage id="perks.flexiRewards.catalogue.header" />
                  </H2>
                )}

                {isWallet && (
                  <>
                    <Spacing marginLeft={-1} marginRight={-1}>
                      <Sticky stickyStyle={{marginTop: "70px", backgroundColor: `${theme.colors.greys.lightest}`, zIndex: 1}}>
                        <Flex paddingLeft paddingRight paddingBottom marginTop={2} alignItems="center" justifyContent="space-between">
                          <H2>
                            <FormattedMessage id="perks.flexiRewards.catalogue.header" />
                          </H2>
                          <ButtonRouterLink id="perks_flexiRewards_backToRewardWallet_button" to="flexi-reward">
                            <FormattedMessage id="perks.flexiRewards.catalogue.cta.title" />
                          </ButtonRouterLink>
                        </Flex>
                      </Sticky>
                    </Spacing>
                    <Body marginTop>
                      <FormattedMessage id="perks.flexiRewards.catalogue.subtitle" />
                    </Body>
                  </>
                )}
              </>
            )}
            <Loading load={load}>
              <H4 marginTop={2} marginBottom>
                <FormattedMessage id="perks.containers.deals.browseBy" />
              </H4>
              <Categories
                categories={categories}
                selectedCategory={category}
                selectedTags={selectedTags}
                onChange={this.handleCategoryOrTagChange}
              />
              <FiltersWrapper direction="column" justifyContent="space-between" alignItems="center" marginBottom={2}>
                {showRegionFilter && (
                  <Col md={4}>
                    <Regions // TODO filter options by business regions (don't list them all)
                      value={selectedRegions || ["UK"]} // TODO get userRegion as default value
                      onChange={this.handleRegionFilterChange}
                      isClearable
                      flush
                    />
                  </Col>
                )}
                {showDealTypeFilter ? (
                  <DealTypesFilter
                    options={dealTypes}
                    typeFromQuery={filters?.type?.uuid}
                    applyFilter={(type) => this.updateSearchParams({type})}
                  />
                ) : null}
                <StyledSortBy
                  icon={IconTypes.filterUpAndDown}
                  options={sortOptions}
                  value={variables.sort!}
                  onClick={(option) => this.updateSearchParams({sort: option.value})}
                />
              </FiltersWrapper>
              <BadCategoryOrTag category={category} tags={tags}>
                <DealListComponent variables={variables}>
                  {(apollo) => {
                    const {results, total} = getDealList(apollo)

                    return (
                      <>
                        <DisplayCount small marginBottom={1}>
                          <FormattedMessage id="perks.categories.search.summary" values={{count: total, results: results?.length}} />
                        </DisplayCount>
                        <DealList deals={results || []} page={DEAL_PAGES.CATEGORY} />
                        <ShowMoreOrThatsAll onNewPage={this.handleNewPage} apollo={apollo} />
                      </>
                    )
                  }}
                </DealListComponent>
              </BadCategoryOrTag>
            </Loading>
          </Col>
        </Container>
        <GlobalRewardWalletModal />
      </Row>
    )
  }
}

export default withPerkOrReward(injectIntl(Deals, {withRef: true}))
